'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function IntakeCTASection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-28 border-t border-border relative overflow-hidden"
      aria-label="Initiate ForensicDesk triage"
    >
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 100%, rgba(220,38,38,0.06) 0%, transparent 70%)' }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div
          className={`border border-border glass-card p-8 md:p-12 lg:p-16 relative overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Top scan line */}
          <div className="absolute top-0 left-0 w-full h-px border-scan" aria-hidden="true" />

          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-2 h-2 bg-primary threat-pulse" />
                <span className="bracket-tag text-primary">[SECURE INTAKE // ENCRYPTED CHANNEL]</span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter uppercase text-foreground leading-tight mb-4">
                Time Is an<br />
                <span className="text-gradient-crimson">Operational Variable.</span>
              </h2>

              <p className="text-muted-foreground leading-relaxed font-light max-w-lg">
                Every hour after a crypto theft incident increases fund dispersion across
                chains and mixers. Submit your incident now for immediate triage assessment.
                Response within 4 hours.
              </p>

              <div className="flex flex-wrap gap-6 mt-6">
                {[
                  { icon: 'ClockIcon', label: '4-Hour Response SLA' },
                  { icon: 'LockClosedIcon', label: 'End-to-End Encrypted' },
                  { icon: 'ShieldCheckIcon', label: 'Attorney-Client Privilege Available' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-accent shrink-0" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 shrink-0 w-full lg:w-auto">
              <Link
                href="/intake"
                className="shine-effect flex items-center justify-center gap-3 bg-primary hover:bg-red-700 text-primary-foreground px-8 py-4 bracket-tag transition-colors duration-200 text-sm min-h-[52px]"
              >
                <span className="w-2 h-2 rounded-full bg-primary-foreground threat-pulse" />
                INITIATE TRIAGE NOW
                <Icon name="ArrowRightIcon" size={14} />
              </Link>
              <Link
                href="/threat-registry"
                className="flex items-center justify-center gap-2 border border-border hover:border-primary text-muted-foreground hover:text-foreground px-8 py-3.5 bracket-tag transition-all duration-200 min-h-[44px]"
              >
                CHECK THREAT REGISTRY
              </Link>
              <p className="bracket-tag text-muted-foreground text-center">
                [NO UPFRONT FEES // ASSESSMENT ONLY]
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}